import {
  ManualNoteTaxType,
  NoteEtaxReasonCode,
  NoteReasonType,
  NoteSettlementMethod,
  NoteStatus,
  NoteType,
} from "../../types/credit-debit-note.type";
import { EXPRESS_PATH } from "../ExpressData/constants";

export const CREDIT_DEBIT_NOTE_MENU_TITLE = "ใบลดหนี้/ใบเพิ่มหนี้";

export const CREDIT_DEBIT_NOTE_PATH = `${EXPRESS_PATH}/credit-debit-notes`;
export const CREDIT_DEBIT_NOTE_NEW_PATH = `${CREDIT_DEBIT_NOTE_PATH}/new`;
export const creditDebitNoteDetailPath = (uuid: string) =>
  `${CREDIT_DEBIT_NOTE_PATH}/${encodeURIComponent(uuid)}`;

/** The ทั้งหมด option of every list filter; dropped before the request is built. */
export const ALL = "all";

type ChipColor = "default" | "primary" | "secondary" | "success" | "error";

export const NOTE_TYPE_LABEL: Record<NoteType, string> = {
  CREDIT: "ใบลดหนี้",
  DEBIT: "ใบเพิ่มหนี้",
};

/** The form's type toggle names the action rather than the document. */
export const NOTE_TYPE_ACTION_LABEL: Record<NoteType, string> = {
  CREDIT: "ลดหนี้",
  DEBIT: "เพิ่มหนี้",
};

export const NOTE_TYPE_COLOR: Record<NoteType, ChipColor> = {
  CREDIT: "secondary",
  DEBIT: "primary",
};

/** สาเหตุการลดหนี้ / สาเหตุการเพิ่มหนี้ — the heading the printed note puts over the reason. */
export const NOTE_REASON_LABEL: Record<NoteType, string> = {
  CREDIT: "สาเหตุการลดหนี้",
  DEBIT: "สาเหตุการเพิ่มหนี้",
};

export const NOTE_STATUS_LABEL: Record<NoteStatus, string> = {
  ISSUED: "ออกแล้ว",
  CANCELLED: "ยกเลิก",
};

export const NOTE_STATUS_COLOR: Record<NoteStatus, ChipColor> = {
  ISSUED: "success",
  CANCELLED: "error",
};

export const NOTE_TYPE_FILTER: { value: NoteType | typeof ALL; label: string }[] = [
  { value: ALL, label: "ทั้งหมด" },
  { value: "CREDIT", label: NOTE_TYPE_LABEL.CREDIT },
  { value: "DEBIT", label: NOTE_TYPE_LABEL.DEBIT },
];

export const NOTE_STATUS_FILTER: { value: NoteStatus | typeof ALL; label: string }[] = [
  { value: ALL, label: "ทั้งหมด" },
  { value: "ISSUED", label: NOTE_STATUS_LABEL.ISSUED },
  { value: "CANCELLED", label: NOTE_STATUS_LABEL.CANCELLED },
];

export const MANUAL_TAX_TYPE_OPTIONS: { value: ManualNoteTaxType; label: string }[] = [
  { value: "IN", label: "ราคารวม VAT" },
  { value: "EX", label: "ราคาไม่รวม VAT" },
];

/** `IN` prices include VAT; `EX`, `N/A` and an unset tax type add VAT on top. */
export const taxTypeLabel = (taxType: string | null | undefined) =>
  taxType === "IN" ? "ราคารวม VAT" : "ราคาไม่รวม VAT";

/** Longest free text the form accepts for the remark and the cancel reason. */
export const NOTE_TEXT_MAX_LENGTH = 500;

/** The server caps a note's reason here: anything longer would be cut off on the PDF. */
export const NOTE_REASON_MAX_LENGTH = 255;

/** The server refuses a longer `search` on GET /credit-debit-notes. */
export const NOTE_SEARCH_MAX_LENGTH = 100;

export const NOTE_REASON_TYPE_LABEL: Record<NoteReasonType, string> = {
  RETURN: "คืนสินค้า",
  DAMAGED: "ชำรุด/หมดอายุ",
  PRICE: "ปรับราคา",
};

/** What a credit note may correct, in the order the form offers it. */
export const CREDIT_REASON_TYPES: NoteReasonType[] = ["RETURN", "DAMAGED", "PRICE"];

/** A debit note only ever raises the price. */
export const DEBIT_REASON_TYPE: NoteReasonType = "PRICE";

/** The only reason that puts goods back into van stock (a credit note's). */
export const STOCK_RETURN_REASON_TYPE: NoteReasonType = "RETURN";

/** Credit-note reasons whose qty counts as taken back from a bill line (the server's rule). */
export const QTY_CREDIT_REASON_TYPES: NoteReasonType[] = ["RETURN", "DAMAGED"];

export const NOTE_SETTLEMENT_LABEL: Record<NoteSettlementMethod, string> = {
  CASH: "เงินสด",
  CREDIT: "เงินเชื่อ",
};

export const NOTE_SETTLEMENT_OPTIONS: NoteSettlementMethod[] = ["CASH", "CREDIT"];

/** The e-Tax purpose codes (ETDA goods set, handbook §7.2) with their meaning. */
export const ETAX_REASON_LABEL: Record<NoteEtaxReasonCode, string> = {
  CDNG01: "ลดราคา",
  CDNG02: "สินค้าชำรุดเสียหาย",
  CDNG04: "คิดราคาผิดสูงกว่าจริง",
  CDNG05: "รับคืนสินค้า",
  CDNG99: "อื่นๆ (ระบุสาเหตุ)",
  DBNG01: "เพิ่มราคา",
  DBNG02: "คิดราคาผิดต่ำกว่าจริง",
  DBNG99: "อื่นๆ (ระบุสาเหตุ)",
};

/**
 * The codes a credit note offers per reason (handbook §7.2, split by Q4: good goods back →
 * CDNG05, damaged → CDNG02, price → CDNG01 / CDNG04), each with "other". The server checks the
 * code against the note type only; this narrows the choice to the ones that fit the reason.
 */
export const CREDIT_ETAX_REASON_CODES: Record<NoteReasonType, NoteEtaxReasonCode[]> = {
  RETURN: ["CDNG05", "CDNG99"],
  DAMAGED: ["CDNG02", "CDNG99"],
  PRICE: ["CDNG01", "CDNG04", "CDNG99"],
};

export const DEBIT_ETAX_REASON_CODES: NoteEtaxReasonCode[] = ["DBNG01", "DBNG02", "DBNG99"];

/** "Other": e-Tax sends the note's reason text as the purpose, so the text must say why. */
export const OTHER_ETAX_REASON_CODES: NoteEtaxReasonCode[] = ["CDNG99", "DBNG99"];

export const NOTE_SETTLEMENT_FILTER: { value: NoteSettlementMethod | typeof ALL; label: string }[] = [
  { value: ALL, label: "ทั้งหมด" },
  { value: "CASH", label: NOTE_SETTLEMENT_LABEL.CASH },
  { value: "CREDIT", label: NOTE_SETTLEMENT_LABEL.CREDIT },
];

export const NOTE_REASON_TYPE_FILTER: { value: NoteReasonType | typeof ALL; label: string }[] = [
  { value: ALL, label: "ทั้งหมด" },
  ...CREDIT_REASON_TYPES.map((reasonType) => ({
    value: reasonType,
    label: NOTE_REASON_TYPE_LABEL[reasonType],
  })),
];

/** Widest note-date range one export reads, both ends counted (the server's cap). */
export const NOTE_EXPORT_MAX_DAYS = 31;
